// <pos-menu-editor/> — 菜單編輯器（owner only）
// 左側 Category 列 + 右側 Item 列，可新增/編輯/刪除
// see architecture.md#L280, L580

import { fetchMenu, fetchMenuItemCosts, upsertCategory, deleteCategory, upsertMenuItem, deleteMenuItem } from '../lib/api.js';

class PosMenuEditor extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.state = {
      categories: [],
      items: [],
      selectedCategoryId: null,
      itemTab: 'active',      // 'active' | 'archived' — Pat 2026-04-23 UX：已下架品項另開一區，避免與在售品項混淆
      loading: true,
      error: null,
    };
  }

  async connectedCallback() {
    this.render();
    await this.reload();
  }

  async reload() {
    this.state.loading = true;
    this.state.error = null;
    this.render();
    try {
      // 2026-04-23 Codex B1 修：cost 拆獨立 endpoint，editor 同時 fetch 兩邊
      // menu-list 走 SW SWR 瞬開；cost map 走 no-store（owner/manager only）
      const [menuData, costData] = await Promise.all([
        fetchMenu({ includeInactive: true }),
        fetchMenuItemCosts().catch(() => ({ costs: {} })),  // 非 owner/manager 會 403，寬容處理
      ]);
      const costs = costData?.costs || {};
      this.state.categories = menuData.categories || [];
      this.state.items = (menuData.items || []).map(i => ({ ...i, cost: costs[i.id] ?? 0 }));
      if (!this.state.selectedCategoryId && this.state.categories[0]) {
        this.state.selectedCategoryId = this.state.categories[0].id;
      }
    } catch (err) {
      this.state.error = err.message_zh || err.message || '讀取失敗';
    } finally {
      this.state.loading = false;
      this.render();
    }
  }

  render() {
    const { categories, items, selectedCategoryId, itemTab, loading, error } = this.state;
    const selected = categories.find(c => c.id === selectedCategoryId);
    const catItems = items.filter(i => i.category_id === selectedCategoryId);
    const activeItems = catItems.filter(i => i.is_available !== false);
    const archivedItems = catItems.filter(i => i.is_available === false);
    const filteredItems = itemTab === 'archived' ? archivedItems : activeItems;

    this.shadowRoot.innerHTML = `
      <style>
        :host { display: block; padding: var(--s-5); max-width: 1100px; margin: 0 auto; }
        h2 { font-family: var(--font-display); color: var(--fg); margin-bottom: var(--s-5); }
        .banner { padding: var(--s-3) var(--s-4); border-radius: var(--r-md); margin-bottom: var(--s-4); }
        .banner.err { background: rgba(220,38,38,.1); color: var(--danger); }
        .banner.loading { background: rgba(59,130,246,.1); color: var(--fg-muted); }
        .grid { display: grid; grid-template-columns: 260px minmax(0, 1fr); gap: var(--s-5); }
        /* 手機：分類面板收到內容上方，單欄堆疊（防兩欄在窄螢幕溢出） */
        @media (max-width: 768px) {
          .grid { grid-template-columns: minmax(0, 1fr); gap: var(--s-4); }
        }
        .pane { background: var(--bg-elevated); border: 1px solid var(--border); border-radius: var(--r-lg); padding: var(--s-4); }
        .pane-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: var(--s-3); }
        .pane-head h3 { font-size: 1rem; color: var(--fg); }
        .list { display: flex; flex-direction: column; gap: var(--s-2); }
        .cat-row, .item-row {
          display: flex; align-items: center; justify-content: space-between;
          padding: var(--s-3); border-radius: var(--r-md); background: var(--bg);
          border: 1px solid transparent; cursor: pointer; transition: .15s;
        }
        .cat-row:hover, .item-row:hover { border-color: var(--border); }
        .cat-row.active { border-color: var(--brand-primary); background: rgba(99,102,241,.08); }
        .cat-row.inactive, .item-row.inactive { opacity: .5; }
        .row-main { flex: 1; min-width: 0; }
        .row-name { font-weight: 600; color: var(--fg); }
        .row-sub { font-size: .8rem; color: var(--fg-muted); margin-top: 2px; }
        .row-actions { display: flex; gap: var(--s-1); }
        .icon-btn {
          padding: var(--s-1) var(--s-2); background: transparent; border: 1px solid var(--border);
          border-radius: var(--r-sm); cursor: pointer; font-size: .8rem;
        }
        .icon-btn:hover { background: var(--bg-elevated); }
        .icon-btn.danger { color: var(--danger); }
        .icon-btn.ok { color: var(--brand-primary); }

        /* tab 列：在售 / 已下架 */
        .tab-row { display: flex; gap: var(--s-1); margin-bottom: var(--s-3); }
        .tab-row button {
          background: transparent; border: 1px solid var(--border); color: var(--fg-muted);
          padding: var(--s-2) var(--s-3); border-radius: var(--r-md); cursor: pointer;
          font-size: .85rem; font-family: inherit;
        }
        .tab-row button[aria-pressed="true"] {
          background: var(--brand-primary); color: white; border-color: var(--brand-primary);
        }
        .tab-row .count { opacity: .7; margin-left: var(--s-1); font-size: .75rem; }
        .empty { color: var(--fg-muted); text-align: center; padding: var(--s-6); font-size: .9rem; }
        .add-btn {
          width: 100%; padding: var(--s-2) var(--s-3); background: var(--brand-primary); color: white;
          border: none; border-radius: var(--r-md); cursor: pointer; font-size: .85rem;
        }
        .add-btn:hover { opacity: .9; }

        /* dialog */
        dialog::backdrop { background: rgba(0,0,0,.5); }
        dialog { border: none; border-radius: var(--r-lg); padding: 0; max-width: 420px; width: 90%; background: var(--bg-elevated); color: var(--fg); }
        .dlg-body { padding: var(--s-5); }
        .dlg-body h3 { margin-bottom: var(--s-4); }
        label { display: block; margin: var(--s-3) 0 var(--s-1); font-size: .85rem; color: var(--fg-muted); }
        input, textarea { width: 100%; padding: var(--s-2) var(--s-3); }
        textarea { resize: vertical; min-height: 60px; }
        .dlg-foot { display: flex; justify-content: flex-end; gap: var(--s-2); margin-top: var(--s-5); }
        .btn { padding: var(--s-2) var(--s-4); border-radius: var(--r-md); cursor: pointer; border: 1px solid var(--border); background: var(--bg); color: var(--fg); }
        .btn.primary { background: var(--brand-primary); color: white; border-color: var(--brand-primary); }
      </style>

      <h2>菜單編輯器</h2>

      ${error ? `<div class="banner err">✗ ${escapeHtml(error)}</div>` : ''}
      ${loading ? `<div class="banner loading">載入中…</div>` : ''}

      <div class="grid">
        <!-- Categories -->
        <div class="pane">
          <div class="pane-head">
            <h3>分類</h3>
          </div>
          <div class="list" id="cat-list">
            ${categories.length === 0 ? '<div class="empty">尚無分類，先建立一個分類吧</div>' : ''}
            ${categories.map(c => `
              <div class="cat-row ${c.id === selectedCategoryId ? 'active' : ''} ${c.is_active === false ? 'inactive' : ''}" data-id="${c.id}">
                <div class="row-main">
                  <div class="row-name">${escapeHtml(c.name)}</div>
                  <div class="row-sub">${items.filter(i => i.category_id === c.id && i.is_available !== false).length} 項在售</div>
                </div>
                <div class="row-actions">
                  <button class="icon-btn" data-act="edit-cat" data-id="${c.id}">✎</button>
                  <button class="icon-btn danger" data-act="del-cat" data-id="${c.id}">✕</button>
                </div>
              </div>
            `).join('')}
          </div>
          <button class="add-btn" style="margin-top:var(--s-3)" data-act="add-cat">+ 新增分類</button>
        </div>

        <!-- Items -->
        <div class="pane">
          <div class="pane-head">
            <h3>${selected ? escapeHtml(selected.name) + ' 的品項' : '品項'}</h3>
          </div>
          ${!selected ? '' : `
            <div class="tab-row" role="tablist">
              <button type="button" role="tab" data-tab="active" aria-pressed="${itemTab === 'active'}">
                在售<span class="count">${activeItems.length}</span>
              </button>
              <button type="button" role="tab" data-tab="archived" aria-pressed="${itemTab === 'archived'}">
                已下架<span class="count">${archivedItems.length}</span>
              </button>
            </div>
          `}
          <div class="list">
            ${!selected ? '<div class="empty">請先在左側選擇分類</div>' :
              filteredItems.length === 0 ?
                `<div class="empty">${itemTab === 'archived' ? '此分類沒有已下架的品項' : '此分類尚無品項'}</div>` :
              filteredItems.map(i => `
                <div class="item-row ${i.is_available === false ? 'inactive' : ''}" data-id="${i.id}">
                  <div class="row-main">
                    <div class="row-name">${escapeHtml(i.name)}</div>
                    <div class="row-sub">NT$ ${i.price}${i.description ? ' · ' + escapeHtml(i.description) : ''}</div>
                  </div>
                  <div class="row-actions">
                    ${itemTab === 'archived' ? `
                      <button class="icon-btn ok" data-act="restore-item" data-id="${i.id}" title="恢復上架">↩ 恢復</button>
                    ` : `
                      <button class="icon-btn" data-act="edit-item" data-id="${i.id}">✎</button>
                      <button class="icon-btn danger" data-act="del-item" data-id="${i.id}">✕</button>
                    `}
                  </div>
                </div>
              `).join('')
            }
          </div>
          ${selected && itemTab === 'active' ? '<button class="add-btn" style="margin-top:var(--s-3)" data-act="add-item">+ 新增品項</button>' : ''}
        </div>
      </div>

      <dialog id="dlg"></dialog>
    `;

    this.wireEvents();
  }

  wireEvents() {
    const root = this.shadowRoot;
    root.querySelectorAll('.cat-row').forEach(el => {
      el.addEventListener('click', (e) => {
        if (e.target.closest('[data-act]')) return;
        this.state.selectedCategoryId = el.dataset.id;
        this.render();
      });
    });

    root.querySelectorAll('[data-tab]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.state.itemTab = btn.dataset.tab;
        this.render();
      });
    });

    root.querySelectorAll('[data-act]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const act = btn.dataset.act;
        const id = btn.dataset.id;
        if (act === 'add-cat') this.openCategoryDialog();
        else if (act === 'edit-cat') this.openCategoryDialog(this.state.categories.find(c => c.id === id));
        else if (act === 'del-cat') this.handleDeleteCategory(id);
        else if (act === 'add-item') this.openItemDialog();
        else if (act === 'edit-item') this.openItemDialog(this.state.items.find(i => i.id === id));
        else if (act === 'del-item') this.handleDeleteItem(id);
        else if (act === 'restore-item') this.handleRestoreItem(id);
      });
    });
  }

  openCategoryDialog(cat = null) {
    const dlg = this.shadowRoot.querySelector('#dlg');
    dlg.innerHTML = `
      <form method="dialog" class="dlg-body">
        <h3>${cat ? '編輯分類' : '新增分類'}</h3>
        <label>名稱</label>
        <input name="name" required maxlength="60" value="${escapeAttr(cat?.name || '')}" />
        <label>排序（數字越小越前）</label>
        <input name="sort_order" type="number" min="0" max="9999" value="${cat?.sort_order ?? 0}" />
        <div class="dlg-foot">
          <button type="button" class="btn" data-close>取消</button>
          <button type="submit" class="btn primary">${cat ? '儲存' : '新增'}</button>
        </div>
      </form>
    `;
    dlg.querySelector('[data-close]').onclick = () => dlg.close();
    dlg.querySelector('form').onsubmit = async (e) => {
      e.preventDefault();
      const fd = new FormData(e.target);
      const payload = {
        name: fd.get('name').trim(),
        sort_order: Number(fd.get('sort_order')) || 0,
        ...(cat?.id ? { id: cat.id } : {}),
      };
      try {
        await upsertCategory(payload);
        dlg.close();
        await this.reload();
      } catch (err) {
        alert(`✗ ${err.message_zh || err.message}`);
      }
    };
    dlg.showModal();
  }

  openItemDialog(item = null) {
    const dlg = this.shadowRoot.querySelector('#dlg');
    dlg.innerHTML = `
      <form method="dialog" class="dlg-body">
        <h3>${item ? '編輯品項' : '新增品項'}</h3>
        <label>品項名稱</label>
        <input name="name" required maxlength="100" value="${escapeAttr(item?.name || '')}" />
        <label>價格 (NT$)</label>
        <input name="price" type="number" min="0" max="99999" step="1" required value="${item?.price ?? ''}" />
        <label>成本 (NT$) <span class="hint">選填 · 用來算毛利率</span></label>
        <input name="cost" type="number" min="0" max="99999" step="0.5" value="${item?.cost ?? ''}" placeholder="0" />
        <label>描述（選填）</label>
        <textarea name="description" maxlength="500">${escapeHtml(item?.description || '')}</textarea>
        <label>排序</label>
        <input name="sort_order" type="number" min="0" max="9999" value="${item?.sort_order ?? 0}" />
        <div class="dlg-foot">
          <button type="button" class="btn" data-close>取消</button>
          <button type="submit" class="btn primary">${item ? '儲存' : '新增'}</button>
        </div>
      </form>
    `;
    dlg.querySelector('[data-close]').onclick = () => dlg.close();
    dlg.querySelector('form').onsubmit = async (e) => {
      e.preventDefault();
      const fd = new FormData(e.target);
      const costRaw = fd.get('cost');
      const payload = {
        name: fd.get('name').trim(),
        price: Number(fd.get('price')),
        cost: costRaw === '' || costRaw == null ? 0 : Number(costRaw),
        description: fd.get('description').trim() || undefined,
        sort_order: Number(fd.get('sort_order')) || 0,
        category_id: this.state.selectedCategoryId,
        ...(item?.id ? { id: item.id } : {}),
      };
      try {
        await upsertMenuItem(payload);
        dlg.close();
        await this.reload();
      } catch (err) {
        alert(`✗ ${err.message_zh || err.message}`);
      }
    };
    dlg.showModal();
  }

  async handleDeleteCategory(id) {
    if (!confirm('確定要刪除這個分類嗎？底下品項不會刪除，只是分類停用。')) return;
    try {
      await deleteCategory(id);
      if (this.state.selectedCategoryId === id) this.state.selectedCategoryId = null;
      await this.reload();
    } catch (err) {
      alert(`✗ ${err.message_zh || err.message}`);
    }
  }

  async handleDeleteItem(id) {
    if (!confirm('確定要下架這個品項嗎？\n\n會移到「已下架」分頁，保留歷史訂單資料，之後可隨時恢復。')) return;
    try {
      await deleteMenuItem(id);
      await this.reload();
    } catch (err) {
      alert(`✗ ${err.message_zh || err.message}`);
    }
  }

  async handleRestoreItem(id) {
    const item = this.state.items.find(i => i.id === id);
    if (!item) return;
    if (!confirm(`恢復「${item.name}」上架？`)) return;
    try {
      // 走 upsert 把 is_available 改回 true（無專用 endpoint，沿用 upsert）
      await upsertMenuItem({
        id: item.id,
        name: item.name,
        price: Number(item.price),
        is_available: true,
      });
      await this.reload();
    } catch (err) {
      alert(`✗ ${err.message_zh || err.message}`);
    }
  }
}

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));
}
function escapeAttr(s) { return escapeHtml(s); }

customElements.define('pos-menu-editor', PosMenuEditor);
